//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// Lists, boards and other drag sources keep their drag state to themselves.
// These helpers put the dragged document on the drag itself, so a drop target
// elsewhere on the page (e.g. a calendar day, which sets a task's deadline)
// knows what it is given.
//
// A drag can also say where it started (its origin, e.g. a calendar). A list
// or board marked as a home for documents (`docHome`) hands such a document,
// dropped on it, back to its origin: a task dragged out of a calendar back to
// its list gets its previous deadline again.

import { type Class, type Doc, type Ref } from '@hcengineering/core'

/**
 * Drag data type carrying `{ _id, _class }` of the document being dragged.
 * @public
 */
export const DOC_DRAG_TYPE = 'application/x-huly-doc'

// A drag shows only its types until the drop, so the class and the origin
// also travel as types of their own.
const DOC_CLASS_DRAG_TYPE = 'application/x-huly-doc-class-'
const DOC_ORIGIN_DRAG_TYPE = 'application/x-huly-doc-origin-'

function typeSafe (value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '-')
}

function classDragType (_class: Ref<Class<Doc>>): string {
  return DOC_CLASS_DRAG_TYPE + typeSafe(_class)
}

function originDragType (origin: string): string {
  return DOC_ORIGIN_DRAG_TYPE + typeSafe(origin)
}

/**
 * @public
 */
export interface DraggedDoc {
  _id: Ref<Doc>
  _class: Ref<Class<Doc>>
}

/**
 * Call in `dragstart` to say which document is being dragged, and optionally
 * where the drag starts (see `onDocReturn`).
 * @public
 */
export function setDocDragData (e: DragEvent, doc: DraggedDoc, origin?: string): void {
  if (e.dataTransfer == null) return
  e.dataTransfer.setData(DOC_DRAG_TYPE, JSON.stringify({ _id: doc._id, _class: doc._class }))
  e.dataTransfer.setData(classDragType(doc._class), doc._class)
  if (origin !== undefined) e.dataTransfer.setData(originDragType(origin), origin)
}

/**
 * Whether the drag started at `origin`. Works in `dragover` too.
 * @public
 */
export function isDocDragFrom (e: DragEvent, origin: string): boolean {
  return e.dataTransfer?.types.includes(originDragType(origin)) ?? false
}

/**
 * Whether the dragged document is of one of the classes. Works in `dragenter`
 * and `dragover`, where the data itself cannot be read yet.
 * @public
 */
export function isDocDragOf (e: DragEvent, classes: Array<Ref<Class<Doc>>>): boolean {
  const types = e.dataTransfer?.types ?? []
  return classes.some((it) => types.includes(classDragType(it)))
}

/**
 * Call in `drop`: the dragged document, if the drag carries one.
 * @public
 */
export function getDocDragData (e: DragEvent): DraggedDoc | undefined {
  const raw = e.dataTransfer?.getData(DOC_DRAG_TYPE) ?? ''
  if (raw === '') return undefined
  try {
    const data = JSON.parse(raw)
    return typeof data?._id === 'string' && typeof data?._class === 'string' ? data : undefined
  } catch {
    return undefined
  }
}

/**
 * Takes back a document dragged out of its origin and dropped on a home of
 * documents of its class.
 * @public
 */
export type DocReturnHandler = (doc: DraggedDoc) => void | Promise<void>

const returnHandlers = new Map<string, DocReturnHandler>()

/**
 * Says what happens to a document dragged from `origin` and dropped on a
 * `docHome`. One handler per origin; returns the function that removes it.
 * @public
 */
export function onDocReturn (origin: string, handler: DocReturnHandler): () => void {
  returnHandlers.set(origin, handler)
  return () => {
    if (returnHandlers.get(origin) === handler) returnHandlers.delete(origin)
  }
}

function returningOrigin (e: DragEvent, classes: Array<Ref<Class<Doc>>>): string | undefined {
  if (!isDocDragOf(e, classes)) return undefined
  return [...returnHandlers.keys()].find((origin) => isDocDragFrom(e, origin))
}

// One drop is handled once, even where homes are nested.
const handledDrops = new WeakSet<DragEvent>()

/**
 * Svelte action for a list or board of documents of `classes` (the exact
 * classes the drag announces, see `setDocDragData`): their home.
 * A document of those classes dragged out of an origin with a return handler
 * (see `onDocReturn`) and dropped here goes back through that handler. While
 * it is held over the home, the home shows the `fz-drop-home` outline. The
 * home's own drag and drop keeps working: it only looks at drags it started.
 * @public
 */
export function docHome (
  node: HTMLElement,
  classes: Array<Ref<Class<Doc>>>
): { update: (classes: Array<Ref<Class<Doc>>>) => void, destroy: () => void } {
  let homeClasses = classes
  let timer: any

  function clearOutline (): void {
    clearTimeout(timer)
    node.classList.remove('fz-drop-home')
  }

  // Captured, so a list row that stops the drop for its own reordering does
  // not hide it from the home.
  function onDragOver (e: DragEvent): void {
    if (returningOrigin(e, homeClasses) === undefined) return
    e.preventDefault()
    if (e.dataTransfer != null) e.dataTransfer.dropEffect = 'move'
    node.classList.add('fz-drop-home')
    clearTimeout(timer)
    // Dragover repeats while the document is held over the home.
    timer = setTimeout(clearOutline, 150)
  }

  function onDrop (e: DragEvent): void {
    clearOutline()
    if (handledDrops.has(e)) return
    const origin = returningOrigin(e, homeClasses)
    const doc = getDocDragData(e)
    if (origin === undefined || doc === undefined) return
    handledDrops.add(e)
    e.preventDefault()
    void returnHandlers.get(origin)?.(doc)
  }

  node.addEventListener('dragover', onDragOver, true)
  node.addEventListener('drop', onDrop, true)
  window.addEventListener('dragend', clearOutline)

  return {
    update (next: Array<Ref<Class<Doc>>>) {
      homeClasses = next
    },
    destroy () {
      clearOutline()
      node.removeEventListener('dragover', onDragOver, true)
      node.removeEventListener('drop', onDrop, true)
      window.removeEventListener('dragend', clearOutline)
    }
  }
}
